import { Fragment, useRef, useState } from 'react';

import { Popover, styled } from '@mui/material';

import {
  IconButton,
  Icons,
  List,
  ListItem,
  SortDirection,
  tokens,
} from '@cast/design-system';
import { useSearchContext } from '@cast/shared-search-ui';
import { SortBy } from '@cast/utils';

const StyledListItemButton = styled(ListItem)(({ theme }) => ({
  ...theme.typography.P12R,
  color: tokens.theme.fg.structural.primary,
  height: 32,
}));

type SortByConfig<T> = Pick<SortBy<T>, 'accessor' | 'sortingType'> & {
  name: string;
  fromName?: string;
  toName?: string;
  order?: SortDirection[];
  testId?: string;
};

type Props<T> = {
  sortBy: SortByConfig<T>[];
};

export const makeSearchableListSorter = <T,>({ sortBy }: Props<T>) => {
  const Sorter = () => {
    const iconButtonRef = useRef<HTMLButtonElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    const { sortingState, setSortingState } = useSearchContext<T>() || {};

    const currentAccessor = sortingState?.sortBy.accessor;
    const currentSortDirection = sortingState?.sortBy.direction;

    return (
      <>
        <Popover
          open={isOpen}
          anchorEl={iconButtonRef.current}
          onClose={() => setIsOpen(false)}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          <List data-testid="list-sort-options">
            {sortBy.map((predicate, index) => {
              const {
                name,
                fromName = 'low',
                toName = 'high',
                accessor,
                sortingType,
                testId = name,
                order = ['asc', 'desc'],
              } = predicate;

              const thisAccessorActive = accessor === currentAccessor;
              const handleClick = (direction: SortDirection) => {
                const newState: { sortBy: SortBy<T> } | undefined =
                  thisAccessorActive && currentSortDirection === direction
                    ? undefined
                    : { sortBy: { accessor, direction, sortingType } };
                setSortingState?.(newState);
                setIsOpen(false);
              };

              return (
                <Fragment key={index}>
                  {order.map((direction) => (
                    <StyledListItemButton
                      key={direction}
                      onClick={() => handleClick(direction)}
                      selected={
                        thisAccessorActive && currentSortDirection === direction
                      }
                      testId={`${testId}-${direction}`}
                    >
                      {`${name} ${direction === 'asc' ? fromName : toName} to ${
                        direction === 'asc' ? toName : fromName
                      }`}
                    </StyledListItemButton>
                  ))}
                </Fragment>
              );
            })}
          </List>
        </Popover>
        <IconButton
          variant="ghost"
          size="sm"
          testId="list-sort-toggle"
          onClick={() => setIsOpen(true)}
          ref={iconButtonRef}
        >
          <Icons.FunnelSimple
            color={
              isOpen || sortingState?.sortBy
                ? tokens.theme.fg.selection.primary
                : undefined
            }
          />
        </IconButton>
      </>
    );
  };

  return Sorter;
};
